import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Award, Users, Calendar, Phone, Mail, Linkedin, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TeamPage() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch team members
        const teamRes = await fetch(`${BACKEND_URL}/api/team`);
        if (teamRes.ok) {
          setTeam(await teamRes.json());
        }

        // Fetch SEO
        const seoRes = await fetch(`${BACKEND_URL}/api/seo/team`);
        if (seoRes.ok) {
          setSeo(await seoRes.json());
        }
      } catch (error) {
        console.error('Error fetching team:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update document title and meta
  useEffect(() => {
    if (seo) {
      document.title = seo.meta_title || 'Our Team - RO Dekho';
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', seo.meta_description);
      }
    }
  }, [seo]);

  const founders = team.filter(m => m.role === 'founder' || m.role === 'co-founder');
  const otherMembers = team.filter(m => m.role !== 'founder' && m.role !== 'co-founder');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-green-500/20 text-green-300 rounded-full text-sm font-medium mb-6">
              Meet Our Expert Team
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              The People Behind <span className="text-green-400">RO Dekho</span>
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              A team of dedicated professionals with over 15 years of combined experience 
              in the water purification industry, committed to delivering pure water solutions.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {[
              { icon: Calendar, value: '15+', label: 'Years Experience' },
              { icon: Users, value: '5000+', label: 'Happy Customers' },
              { icon: Award, value: '10+', label: 'Service Awards' },
              { icon: CheckCircle, value: '99%', label: 'Satisfaction Rate' }
            ].map((stat, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <stat.icon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <div className="text-3xl font-bold">{stat.value}</div>
                <div className="text-sm text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Arpsan Lifestyle Technology Pvt Ltd
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Operating under the brand name <strong className="text-blue-600">RO Dekho</strong>, 
              we are Jaipur's most trusted water purification service provider, 
              dedicated to ensuring every family has access to clean and safe drinking water.
            </p>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      {founders.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Founders</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Visionary leaders who built RO Dekho with a commitment to quality and customer satisfaction
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {founders.map((member) => (
                  <div 
                    key={member.member_id} 
                    className="bg-white rounded-2xl shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="md:flex">
                      <div className="md:w-2/5">
                        <div className="h-64 md:h-full bg-gradient-to-br from-blue-600 to-blue-800 relative">
                          <img
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                        </div>
                      </div>
                      <div className="md:w-3/5 p-6">
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-3">
                          {member.designation}
                        </span>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                          <Calendar className="w-4 h-4" />
                          <span>{member.experience_years}+ years experience</span>
                        </div>

                        <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-4">
                          {member.bio}
                        </p>

                        {/* Skills */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {member.skills.slice(0, 3).map((skill, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>

                        {/* Contact */}
                        <div className="flex gap-3">
                          {member.email && (
                            <a 
                              href={`mailto:${member.email}`}
                              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Mail className="w-5 h-5" />
                            </a>
                          )}
                          {member.phone && (
                            <a 
                              href={`tel:${member.phone}`}
                              className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              <Phone className="w-5 h-5" />
                            </a>
                          )}
                          {member.linkedin && (
                            <a 
                              href={member.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Linkedin className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Team Members Section */}
      {otherMembers.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Team</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Dedicated professionals working together to deliver the best water purification solutions
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherMembers.map((member) => (
                <div 
                  key={member.member_id}
                  className="bg-white rounded-xl border hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-200">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-block px-3 py-1 bg-white/90 text-blue-700 text-sm font-medium rounded-full">
                        {member.designation}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{member.experience_years}+ years experience</p>
                    
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {member.bio}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {member.skills.slice(0, 3).map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Values Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do at RO Dekho
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Quality First',
                description: 'We never compromise on the quality of our products and services. Every RO system we install and every spare part we use is 100% genuine.',
                icon: '🏆'
              },
              {
                title: 'Customer Satisfaction',
                description: 'Our customers are our priority. We go above and beyond to ensure every interaction leaves you satisfied and your water pure.',
                icon: '💯'
              },
              {
                title: 'Expert Service',
                description: 'Our certified technicians bring years of experience to every service call. We diagnose accurately and fix issues right the first time.',
                icon: '🔧'
              }
            ].map((value, idx) => (
              <div key={idx} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Experience the RO Dekho Difference?
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            Join 5000+ happy customers who trust us for their water purification needs
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+919887068888"
              className="px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl transition-colors"
            >
              Call Us Now
            </a>
            <a
              href="https://wa.me/919887068888"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/30 transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default TeamPage;
